/**
 * Platform health, quiet while there is nothing to say (§16.3, AC-026, AC-032).
 *
 * The full chip set returns the moment anything stops holding. A warning that
 * is always on screen is furniture, and the eye stops seeing it exactly when it
 * starts mattering — so the predicate below is deliberately strict, and
 * loosening it to make a screen look calmer would destroy the whole signal.
 */

import type { ReactNode } from 'react';

import { divergences } from '../../logic/view-phase.js';
import { formatAgeMs, freshnessLabel } from '../../logic/format.js';
import { useStoreState } from '../store-context.js';
import { Chip } from './state.js';
import type { Tone } from '../../logic/display-matrix.js';

export function HealthLine(): ReactNode {
  const scene = useStoreState((state) => state.scene);
  const stream = useStoreState((state) => state.stream);

  const nominal =
    stream.status === 'live' &&
    stream.notice === null &&
    scene.status === 'ready' &&
    scene.value.mode === 'live' &&
    scene.value.warnings.length === 0 &&
    divergences(scene.value).length === 0 &&
    scene.value.sourceFreshness.board.freshness === 'fresh' &&
    scene.value.sourceFreshness.execution.freshness === 'fresh';

  if (nominal) {
    return (
      <div
        className="health-banner health-banner-quiet"
        aria-label="Platform health"
        title="Live — the board and the team are both being read right now"
      >
        <span className="health-dot" aria-hidden="true" />
        <span className="health-quiet-text">Live</span>
      </div>
    );
  }

  const chips: ReactNode[] = [];
  chips.push(<Chip key="stream" tone={streamTone(stream.status)} text={stream.status.toUpperCase()} />);

  if (scene.status === 'ready') {
    for (const [name, value] of [
      ['BOARD', scene.value.sourceFreshness.board],
      ['EXECUTION', scene.value.sourceFreshness.execution],
    ] as const) {
      const tone: Tone = value.freshness === 'fresh' ? 'active' : value.freshness === 'stale' ? 'wait' : 'unknown';
      chips.push(
        <Chip key={name} tone={tone} text={`${name} ${freshnessLabel(value.freshness)} · ${formatAgeMs(value.ageMs)}`} />,
      );
    }
    const diverged = divergences(scene.value);
    if (diverged.length > 0) {
      chips.push(<Chip key="diverged" tone="danger" text={`${String(diverged.length)} DIVERGED`} />);
    }
  } else if (scene.status === 'error') {
    chips.push(<Chip key="error" tone="danger" text="PROJECTION ERROR" />);
  }

  return (
    <div className="health-banner" aria-label="Platform health">
      <div className="health-chips">{chips}</div>
      {stream.notice === null ? null : <p className="health-notice">{stream.notice}</p>}
      {scene.status === 'ready' && scene.value.warnings.length > 0 ? (
        <ul className="health-warnings">
          {scene.value.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function streamTone(status: string): Tone {
  switch (status) {
    case 'live':
      return 'active';
    case 'reconnecting':
      return 'wait';
    case 'error':
      return 'danger';
    case 'reset':
      return 'unknown';
    default:
      return 'neutral';
  }
}
